import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth } from "../lib/firebase";
import { state } from "../lib/state";
import { apiRequest } from "../lib/api";
import { showToast, formatError } from "../lib/dom";
import { validateCredentials, signInWithProvider } from "../lib/auth";
import { onScreenChange, setActiveScreen } from "../lib/router";
import { trackEvent } from "../lib/analytics";
import { applyBillingDataUI, openPortal, refreshBillingState } from "./billing";

function errorCode(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = (error as { code?: unknown }).code;
    if (code) return String(code);
  }
  return undefined;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export function applyAuthStateUI(): void {
  const signedIn = Boolean(state.user);

  const accountLayout = document.getElementById("account-layout");
  const accountAuthPanel = document.getElementById("account-auth-panel");
  const accountStatePanel = document.getElementById("account-state-panel");
  const accountInvitePanel = document.getElementById("account-invite-panel");
  const accountNavLinks = document.querySelectorAll<HTMLElement>(
    '.nav-link[data-screen="account"]',
  );
  const ctaButton = document.getElementById("cta-get-started");
  const authStateCard = document.getElementById("auth-state");
  const accountUserId = document.getElementById("account-user-id");
  const copyUserIdButton = document.getElementById("copy-user-id-button") as HTMLButtonElement | null;
  const logoutButton = document.getElementById("logout-button") as HTMLButtonElement | null;
  const portalButton = document.getElementById("portal-button") as HTMLButtonElement | null;
  const sessionTitle = document.getElementById("session-title");
  const sessionCopy = document.getElementById("session-copy");
  const sessionCardTitle = document.getElementById("session-card-title");
  const sessionCardCopy = document.getElementById("session-card-copy");

  document.body.classList.toggle("is-authed", signedIn);
  accountLayout?.classList.toggle("is-signed-in", signedIn);
  if (accountAuthPanel) accountAuthPanel.hidden = signedIn;
  if (accountStatePanel) accountStatePanel.hidden = !signedIn;
  if (accountInvitePanel) accountInvitePanel.hidden = !signedIn;

  accountNavLinks.forEach(link => {
    link.textContent = signedIn ? "Account" : "Sign In";
  });
  if (ctaButton) ctaButton.textContent = signedIn ? "Try On" : "Get Started";
  authStateCard?.classList.toggle("logged-out", !signedIn);
  if (accountUserId) accountUserId.textContent = signedIn ? state.user?.uid || "-" : "-";
  if (copyUserIdButton) copyUserIdButton.disabled = !signedIn || !state.user?.uid;
  if (logoutButton) logoutButton.disabled = !signedIn;
  if (portalButton) portalButton.disabled = !signedIn || !state.billingConfigured;

  if (signedIn) {
    const email = state.user?.email || "Signed in";
    if (sessionTitle) sessionTitle.textContent = email;
    if (sessionCopy)
      sessionCopy.textContent = "Welcome back. Try on a new outfit or hairstyle.";
    if (sessionCardTitle) sessionCardTitle.textContent = email;
    if (sessionCardCopy)
      sessionCardCopy.textContent = "Pick a plan or start trying on outfits.";
  } else {
    if (sessionTitle) sessionTitle.textContent = "Not signed in";
    if (sessionCopy)
      sessionCopy.textContent =
        "Sign in to start trying on outfits and manage your plan.";
    if (sessionCardTitle) sessionCardTitle.textContent = "Not signed in";
    if (sessionCardCopy)
      sessionCardCopy.textContent = "Sign in with your email and password.";
    applyBillingDataUI({
      credits: 0,
      plans: "Free",
      billingReady: state.billingConfigured,
    });
  }
}

export async function redeemInvite(token: string): Promise<void> {
  if (!state.user || !state.idToken) {
    showToast("Sign in before redeeming an invite.", true);
    setActiveScreen("account");
    return;
  }

  const inviteTokenInput = document.getElementById("invite-token") as HTMLInputElement | null;

  trackEvent("invite_redeem_started");
  try {
    await apiRequest("/api/migration/invites/redeem", {
      method: "POST",
      body: JSON.stringify({ token }),
    });
    trackEvent("redeemed_promo_code", { code: token });
    showToast("Welcome back! Your invite has been redeemed.");
    if (inviteTokenInput) inviteTokenInput.value = "";
    await refreshBillingState();
  } catch (error: unknown) {
    trackEvent("invite_redeem_failed", {
      error_code: errorCode(error),
      error_message: errorMessage(error),
    });
    showToast(formatError(error), true);
  }
}

export function bind(): void {
  const authForm = document.getElementById("auth-form") as HTMLFormElement | null;
  const emailInput = document.getElementById("email") as HTMLInputElement | null;
  const passwordInput = document.getElementById("password") as HTMLInputElement | null;
  const signupButton = document.getElementById("signup-button");
  const resetButton = document.getElementById("reset-button");
  const googleButton = document.getElementById("google-button");
  const logoutButton = document.getElementById("logout-button");
  const portalButton = document.getElementById("portal-button");
  const buyCreditsButton = document.getElementById("buy-credits-button");
  const copyUserIdButton = document.getElementById("copy-user-id-button");
  const inviteForm = document.getElementById("invite-form") as HTMLFormElement | null;
  const inviteTokenInput = document.getElementById("invite-token") as HTMLInputElement | null;

  authForm?.addEventListener("submit", async event => {
    event.preventDefault();
    if (!emailInput || !passwordInput) return;
    const credentials = validateCredentials(emailInput, passwordInput);
    if (!credentials) return;

    trackEvent("sign_in_started", { method: "email" });
    try {
      await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      trackEvent("sign_in_completed", { method: "email" });
      showToast("Signed in.");
    } catch (error: unknown) {
      trackEvent("sign_in_failed", {
        method: "email",
        error_code: errorCode(error),
        error_message: errorMessage(error),
      });
      console.warn("Email sign-in failed:", error);
      showToast(formatError(error), true);
    }
  });

  signupButton?.addEventListener("click", async () => {
    if (!emailInput || !passwordInput) return;
    const credentials = validateCredentials(emailInput, passwordInput);
    if (!credentials) return;

    trackEvent("sign_up_started", { method: "email" });
    try {
      await createUserWithEmailAndPassword(auth, credentials.email, credentials.password);
      trackEvent("sign_up_completed", { method: "email" });
      showToast("Account created.");
    } catch (error: unknown) {
      trackEvent("sign_up_failed", {
        method: "email",
        error_code: errorCode(error),
        error_message: errorMessage(error),
      });
      console.warn("Account creation failed:", error);
      showToast(formatError(error), true);
    }
  });

  resetButton?.addEventListener("click", async () => {
    if (!emailInput || !passwordInput) return;
    const credentials = validateCredentials(emailInput, passwordInput, {
      requirePassword: false,
    });
    if (!credentials) return;

    trackEvent("password_reset_requested");
    try {
      await sendPasswordResetEmail(auth, credentials.email);
      showToast("Password reset email sent.");
    } catch (error: unknown) {
      showToast(formatError(error), true);
    }
  });

  googleButton?.addEventListener("click", async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    trackEvent("sign_in_started", { method: "google" });
    await signInWithProvider(provider, "Google");
  });

  logoutButton?.addEventListener("click", async () => {
    trackEvent("sign_out_tapped");
    await signOut(auth);
    showToast("Signed out.");
  });

  portalButton?.addEventListener("click", () => {
    void openPortal();
  });

  buyCreditsButton?.addEventListener("click", () => {
    trackEvent("account_buy_credits_tapped");
    setActiveScreen("pricing");
    requestAnimationFrame(() => {
      document.getElementById("coins-grid")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  });

  copyUserIdButton?.addEventListener("click", async () => {
    const userId = state.user?.uid;
    if (!userId) return;
    try {
      await navigator.clipboard.writeText(userId);
      showToast("User ID copied.");
    } catch (error: unknown) {
      showToast(formatError(error), true);
    }
  });

  inviteForm?.addEventListener("submit", async event => {
    event.preventDefault();
    const token = inviteTokenInput?.value.trim();
    if (!token) {
      showToast("Paste an invite token first.", true);
      return;
    }
    await redeemInvite(token);
  });

  onScreenChange(screen => {
    if (screen === "account" && !state.user) {
      trackEvent("auth_shown");
    }
  });
}
